import { Box, Flex, Text } from "@chakra-ui/react";
import { forwardRef, type ReactNode } from "react";
import type { AccentRole } from "theme/accents";
import { Card } from "./card";
import { PreHeader } from "./pre-header";

/**
 * KpiTile — hero metric block.
 *
 * Layout:
 *   PRE-HEADER LABEL
 *   {value}              {delta chip}
 *   {sublabel / sparkline}
 *
 * Use for the dashboard KPI hero row, customer-detail headers, and
 * server-detail summary blocks. The big numeric value uses `font-variant-
 * numeric: tabular-nums` so digits align cleanly when stacked.
 */

interface KpiTileProps {
  label: string;
  value: ReactNode;
  /** Small secondary line below the value (e.g. "vs last 7 days"). */
  sublabel?: ReactNode;
  /** Delta chip rendered to the right of the value (e.g. "+12%"). */
  delta?: ReactNode;
  /** Trend direction — affects delta chip colour via accent tokens. */
  trend?: "up" | "down" | "flat";
  /** Card visual treatment — defaults to flat to match the rest of the app. */
  tone?: "flat" | "elevated" | "accent";
  /** When tone="accent", which accent role to use. */
  accent?: AccentRole;
  /** Optional decoration slot below the value (e.g. sparkline). */
  decoration?: ReactNode;
  /** Optional click handler — turns the tile into a link/button visually. */
  onClick?: () => void;
}

const trendChipStyles: Record<
  "up" | "down" | "flat",
  { bg: string; color: string }
> = {
  up: { bg: "var(--accent-healthy-wash)", color: "var(--accent-healthy-fg)" },
  down: { bg: "var(--accent-risk-wash)", color: "var(--accent-risk-fg)" },
  flat: { bg: "var(--accent-neutral-wash)", color: "var(--accent-neutral-fg)" },
};

export const KpiTile = forwardRef<HTMLDivElement, KpiTileProps>(
  function KpiTile(
    {
      label,
      value,
      sublabel,
      delta,
      trend = "flat",
      tone = "flat",
      accent,
      decoration,
      onClick,
    },
    ref,
  ) {
    return (
      <Card.Root
        ref={ref}
        tone={tone}
        accent={accent}
        onClick={onClick}
        cursor={onClick ? "pointer" : "default"}
        transition="box-shadow 160ms ease, transform 160ms ease"
        _hover={
          onClick
            ? {
                boxShadow: "var(--shadow-card-hover)",
                transform: "translateY(-1px)",
              }
            : undefined
        }
      >
        <Box p={5}>
          <PreHeader size="md">{label}</PreHeader>
          <Flex align="baseline" justify="space-between" gap={3} mt={3}>
            <Text
              as="span"
              fontFamily="var(--font-heading)"
              fontSize={{ base: "40px", lg: "48px" }}
              fontWeight={400}
              lineHeight="1"
              letterSpacing="-0.04em"
              color="var(--text-primary)"
              style={{ fontVariantNumeric: "tabular-nums" }}
            >
              {value}
            </Text>
            {delta && (
              <Box
                as="span"
                px="10px"
                py="4px"
                borderRadius="var(--radius-pill)"
                fontFamily="var(--font-mono)"
                fontSize="11px"
                fontWeight={500}
                letterSpacing="0.04em"
                {...trendChipStyles[trend]}
                style={{ fontVariantNumeric: "tabular-nums" }}
              >
                {delta}
              </Box>
            )}
          </Flex>
          {sublabel && (
            <Text
              mt={3}
              fontSize="13px"
              color="var(--text-secondary-color)"
              lineHeight="1.4"
            >
              {sublabel}
            </Text>
          )}
          {decoration && <Box mt={4}>{decoration}</Box>}
        </Box>
      </Card.Root>
    );
  },
);
